/**
 * Geometry helpers shared by the SVG-drawn widgets.
 *
 * They exist so the arithmetic that turns values into coordinates is written
 * once: a line chart, an area chart and a sparkline are the same polyline, and
 * three copies of the same `(i / (n - 1)) * 100` is how they drift apart.
 */

/** Series values → a `d` attribute across a 0-100 viewBox, y inverted. */
export const polylinePath = (values) => {
  /*
    A `null` is a gap, not a zero (CT-5641).

    Two series of a `series_chart` need not carry the same x values — a region
    with no sales in September simply has no point for it — so the adapter
    aligns them and leaves a `null` where one is missing. Drawing that as zero
    would invent a collapse to the floor; drawing it as `NaN` breaks the whole
    path. So the line breaks and resumes: each run of real values is its own
    `M … L …` segment.
  */
  const at = (index) => (index / Math.max(1, values.length - 1)) * 100;
  const segments = [];
  let run = [];

  values.forEach((value, index) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      if (run.length) segments.push(run);
      run = [];
      return;
    }
    run.push(`${at(index)},${100 - value}`);
  });
  if (run.length) segments.push(run);

  return segments.map((segment) => `M ${segment.join(' L ')}`).join(' ');
};

/**
 * One band of a stacked area — the ribbon between two cumulative boundaries.
 *
 * A stacked area is not several area charts drawn on top of each other. Each
 * series occupies the strip between where the stack stood before it and where
 * it stands after, so the bands tile without hiding one another and the top
 * edge of the last one is the total.
 *
 * Both boundaries arrive already scaled to the 0-100 viewBox. The path runs
 * along the upper edge left to right and back along the lower edge, which is
 * what closes it without a baseline assumption: the floor of a band is the
 * series below it, not zero.
 *
 * Gaps are not supported here on purpose — a stack with a hole in the middle
 * has no defined floor for the band above it. The caller fills them first
 * (`fillGaps: 'ZERO'`), which is also what the contract's `display.fill_gaps`
 * asks for on the stacked types.
 */
export const stackedBandPath = (lower, upper) => {
  if (!upper.length) return '';
  const at = (index) => (index / Math.max(1, upper.length - 1)) * 100;
  const top = upper.map((value, index) => `${at(index)},${100 - value}`);
  const bottom = lower
    .map((value, index) => `${at(index)},${100 - value}`)
    .reverse();
  return `M ${top.join(' L ')} L ${bottom.join(' L ')} Z`;
};

/**
 * Percentages → `conic-gradient` stops.
 *
 * Values are used as given: a caller that has not normalised its slices to 100
 * gets a ring with a gap, which is the honest rendering of an incomplete
 * breakdown rather than a silently rescaled one.
 */
export const conicStops = (slices, colorAt) => {
  let cursor = 0;
  return slices
    .map((slice, index) => {
      const from = cursor * 3.6;
      cursor += slice;
      return `${colorAt(index)} ${from}deg ${cursor * 3.6}deg`;
    })
    .join(', ');
};

/** Share of the largest value, 0-1 — how a map or heat map shades against its own peak. */
export const shareOfPeak = (values) => {
  const peak = Math.max(...values, 0);
  return (value) => (peak > 0 ? value / peak : 0);
};

/** `value` as a percentage of `total`, rounded, guarding a zero total. */
export const percentOf = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);
